import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import Cropper from "react-easy-crop";
import API from "../../api";
import toastr from "toastr";
import { useAuth } from "../../contexts/AuthContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Prefix relative paths from the backend with the API base URL */
const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3000";

function getPhotoUrl(photo: string | File | undefined | null): string | null {
  if (!photo) return null;
  if (photo instanceof File) return null; // handled separately via FileReader
  if (photo.startsWith("http://") || photo.startsWith("https://") || photo.startsWith("blob:")) {
    return photo;
  }
  // Relative path — prepend API base
  return `${API_BASE}${photo.startsWith("/") ? "" : "/"}${photo}`;
}

// ─── Crop helpers ─────────────────────────────────────────────────────────────

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

async function getCroppedImg(imageSrc: string, pixelCrop: CropArea): Promise<File> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d")!;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise<File>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error("Canvas is empty")); return; }
      resolve(new File([blob], "profile_photo.jpg", { type: "image/jpeg" }));
    }, "image/jpeg", 0.92);
  });
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserData {
  id?: number;
  user_type_id: string;
  prefix: string;
  first_name: string;
  last_name: string;
  username?: string;
  email: string;
  age: string;
  sex: string;
  account_no: string;
  aadhar_no: string;
  contact_no: string;
  profile_photo?: string | File;
  address: string;
  status?: string;
  password?: string;
  salary_percentage?: string;
  salary_duration_type?: string;
  joining_date?: string;
  relieve_date?: string;
  service_details?: string;
}

interface PatientPrefix {
  id: string;
  prefix_name: string;
}

interface UserModalProps {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  userData?: UserData | null;
  onSuccess: () => void;
}

const GENDERS = ["Male", "Female", "Other"];

// ─── Crop Modal ───────────────────────────────────────────────────────────────

interface CropModalProps {
  imageSrc: string;
  onCancel: () => void;
  onCropDone: (file: File, preview: string) => void;
}

const CropModal: React.FC<CropModalProps> = ({ imageSrc, onCancel, onCropDone }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_: unknown, pixels: CropArea) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleDone = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const file = await getCroppedImg(imageSrc, croppedAreaPixels);
      const preview = URL.createObjectURL(file);
      onCropDone(file, preview);
    } catch {
      toastr.error("Failed to crop image");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 10,
          width: 420,
          maxWidth: "95vw",
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #dee2e6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>
            <i className="fa fa-crop me-2 text-primary" />
            Crop Profile Photo
          </span>
          <button className="btn-close btn-sm" onClick={onCancel} />
        </div>

        {/* Crop area */}
        <div style={{ position: "relative", height: 300, background: "#1a1a1a" }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        {/* Zoom slider */}
        <div style={{ padding: "12px 20px 0" }}>
          <label className="small text-muted mb-1 d-block">Zoom</label>
          <input
            type="range"
            className="form-range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>

        {/* Actions */}
        <div
          style={{
            padding: "12px 16px",
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleDone} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Cropping…
              </>
            ) : (
              <>
                <i className="fa fa-check me-1" />
                Apply Crop
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const UserModal: React.FC<UserModalProps> = ({ show, onClose, mode, userData, onSuccess }) => {
  const { user: authUser } = useAuth();
  const authUserTypeId = Number(authUser?.user_type_id || 0);

  const defaultForm: UserData = {
    user_type_id: String(authUserTypeId + 1),
    prefix: "",
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    age: "",
    sex: "",
    account_no: "",
    aadhar_no: "",
    contact_no: "",
    profile_photo: "",
    address: "",
    status: "1",
    password: "",
    salary_percentage: "",
    salary_duration_type: "",
    joining_date: "",
    relieve_date: "",
    service_details: "",
  };

  const [activeTab, setActiveTab]   = useState<"basic" | "employment">("basic");
  const [formData, setFormData]     = useState<UserData>(defaultForm);
  const [prefixes, setPrefixes]     = useState<PatientPrefix[]>([]);
  const [loading, setLoading]       = useState(false);
  const [errors, setErrors]         = useState<Partial<Record<keyof UserData, string>>>({});
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [tillDate, setTillDate]     = useState(false);

  // Crop state
  const [cropSrc, setCropSrc]       = useState<string | null>(null);
  const fileInputRef                = useRef<HTMLInputElement>(null);

  // ── Fetch prefixes ──────────────────────────────────────────────────────────
  const fetchPrefixes = async () => {
    try {
      const res = await API.get("/patients/getPrefixes");
      setPrefixes(res.data.data || []);
    } catch {
      // silently fail
    }
  };

  // ── Reset on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    fetchPrefixes();

    if (mode === "edit" && userData) {
      const isRelieveDateTillDate =
        !userData.relieve_date || userData.relieve_date === "till_date";
      setTillDate(isRelieveDateTillDate);
      setFormData({
        id: userData.id,
        user_type_id: String(authUserTypeId + 1),
        prefix: userData.prefix || "",
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        username: userData.username || deriveUsername(userData.email || ""),
        email: userData.email || "",
        age: userData.age || "",
        sex: userData.sex || "",
        aadhar_no: userData.aadhar_no || "",
        account_no: userData.account_no || "",
        contact_no: userData.contact_no || "",
        profile_photo: "",
        address: userData.address || "",
        status: userData.status || "1",
        password: "",
        salary_percentage: userData.salary_percentage || "",
        salary_duration_type: userData.salary_duration_type || "",
        joining_date: userData.joining_date || "",
        relieve_date: isRelieveDateTillDate ? "" : userData.relieve_date || "",
        service_details: userData.service_details || "",
      });

      // ── FIX: use getPhotoUrl to build the correct full URL ──────────────────
      if (typeof userData.profile_photo === "string" && userData.profile_photo) {
        setPhotoPreview(getPhotoUrl(userData.profile_photo));
      } else {
        setPhotoPreview(null);
      }
    } else {
      setFormData({ ...defaultForm, user_type_id: String(authUserTypeId + 1) });
      setPhotoPreview(null);
      setTillDate(false);
    }

    setErrors({});
    setShowPassword(false);
    setActiveTab("basic");
    setCropSrc(null);
  }, [mode, userData, show]);

  // ── Body scroll lock ────────────────────────────────────────────────────────
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const deriveUsername = (email: string) =>
    email.includes("@") ? email.split("@")[0] : email;

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserData, string>> = {};

    if (!formData.prefix)                  newErrors.prefix = "Prefix is required";
    if (!formData.first_name.trim())       newErrors.first_name = "First name is required";
    if (!formData.last_name.trim())        newErrors.last_name = "Last name is required";
    if (!formData.email.trim())            newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";
    if (!formData.age)                     newErrors.age = "Age is required";
    else if (isNaN(Number(formData.age)) || Number(formData.age) < 1 || Number(formData.age) > 120)
      newErrors.age = "Enter a valid age (1–120)";
    if (!formData.sex)                     newErrors.sex = "Gender is required";
    if (!formData.aadhar_no.trim())        newErrors.aadhar_no = "Aadhaar number is required";
    else if (!/^\d{12}$/.test(formData.aadhar_no))
      newErrors.aadhar_no = "Aadhaar must be exactly 12 digits";
    if (!formData.contact_no.trim())       newErrors.contact_no = "Contact number is required";
    else if (!/^\d{10}$/.test(formData.contact_no))
      newErrors.contact_no = "Contact number must be 10 digits";
    if (!formData.address.trim())          newErrors.address = "Address is required";

    if (mode === "add") {
      if (!formData.password)              newErrors.password = "Password is required";
      else if (formData.password.length < 8)
        newErrors.password = "Password must be at least 8 characters";
    } else {
      if (formData.password && formData.password.length < 8)
        newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);

    const basicKeys: (keyof UserData)[] = [
      "prefix", "first_name", "last_name", "email", "age", "sex",
      "account_no", "aadhar_no", "contact_no", "address", "password",
    ];
    if (basicKeys.some((k) => newErrors[k])) setActiveTab("basic");

    return Object.keys(newErrors).length === 0;
  };

  // ── Input change ────────────────────────────────────────────────────────────
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "email") updated.username = deriveUsername(value);
      return updated;
    });
    if (errors[name as keyof UserData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // ── File input → open crop modal ────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toastr.error("Only JPG, PNG, WEBP, GIF images are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      // Allow up to 5 MB before crop (crop will reduce size)
      toastr.error("Image must be less than 5MB");
      return;
    }

    // Read as data URL and open the crop modal
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropSrc(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  // ── After crop is confirmed ─────────────────────────────────────────────────
  const handleCropDone = (file: File, preview: string) => {
    setFormData((prev) => ({ ...prev, profile_photo: file }));
    setPhotoPreview(preview);
    setCropSrc(null);
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const payload = new FormData();

      (Object.keys(formData) as (keyof UserData)[]).forEach((key) => {
        if (key === "profile_photo") {
          if (formData.profile_photo instanceof File) {
            payload.append("profile_photo", formData.profile_photo);
          }
        } else if (formData[key] !== undefined) {
          payload.append(key, String(formData[key] ?? ""));
        }
      });

      payload.set("user_type_id", String(authUserTypeId + 1));
      payload.set("username", deriveUsername(formData.email));

      if (tillDate) payload.set("relieve_date", "till_date");

      if (mode === "add") {
        payload.set("status", "1");
        await API.post("/users/createUser", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toastr.success("User added successfully!");
      } else if (mode === "edit" && formData.id) {
        await API.put(`/users/updateUser/${formData.id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toastr.success("User updated successfully!");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      toastr.error(error.response?.data?.message || "Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  // ── Tab: Basic ──────────────────────────────────────────────────────────────
  const renderBasicTab = () => (
    <div>
      {/* Profile Photo Upload */}
      <div className="d-flex align-items-center mb-3 gap-3">
        <div
          style={{
            width: 70,
            height: 70,
            borderRadius: "50%",
            border: "2px dashed #ced4da",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f8f9fa",
            cursor: "pointer",
            flexShrink: 0,
          }}
          onClick={() => fileInputRef.current?.click()}
          title="Click to upload photo"
        >
          {photoPreview ? (
            <img
              src={photoPreview}
              alt="Profile"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <i className="fa fa-camera text-muted" style={{ fontSize: 20 }} />
          )}
        </div>

        <div>
          <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>Profile Photo</div>
          <small className="text-muted">JPG, PNG, GIF, WEBP · max 5MB · will be cropped</small>
          <div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mt-1"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="fa fa-upload me-1" /> Upload &amp; Crop
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>
      </div>

      {/* Prefix + First Name + Last Name */}
      <Row>
        <Col md={2}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              Prefix <span className="text-danger">*</span>
            </Form.Label>
            <select
              className={`form-select form-select-sm ${errors.prefix ? "is-invalid" : ""}`}
              name="prefix"
              value={formData.prefix}
              onChange={handleInputChange}
            >
              <option value="">Prefix</option>
              {prefixes.map((p) => (
                <option key={p.id} value={p.prefix_name}>
                  {p.prefix_name}
                </option>
              ))}
            </select>
            {errors.prefix && (
              <div className="invalid-feedback d-block small">{errors.prefix}</div>
            )}
          </Form.Group>
        </Col>
        <Col md={5}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              First Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="first_name"
              placeholder="First name"
              value={formData.first_name}
              onChange={handleInputChange}
              isInvalid={!!errors.first_name}
              size="sm"
            />
            <Form.Control.Feedback type="invalid">{errors.first_name}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={5}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              Last Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="last_name"
              placeholder="Last name"
              value={formData.last_name}
              onChange={handleInputChange}
              isInvalid={!!errors.last_name}
              size="sm"
            />
            <Form.Control.Feedback type="invalid">{errors.last_name}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Email + Username */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              Email <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              name="email"
              placeholder="Enter email"
              value={formData.email}
              onChange={handleInputChange}
              isInvalid={!!errors.email}
              size="sm"
            />
            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">Username</Form.Label>
            <Form.Control
              type="text"
              name="username"
              placeholder="Auto-generated from email"
              value={formData.username || ""}
              size="sm"
              readOnly
            />
            <small className="text-muted">Generated from email</small>
          </Form.Group>
        </Col>
      </Row>

      {/* Age + Gender */}
      <Row>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              Age <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              name="age"
              placeholder="Age"
              value={formData.age}
              onChange={handleInputChange}
              isInvalid={!!errors.age}
              min={1}
              max={120}
              size="sm"
            />
            <Form.Control.Feedback type="invalid">{errors.age}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={8}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              Gender <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex gap-3 mt-1">
              {GENDERS.map((option) => (
                <Form.Check
                  key={option}
                  type="radio"
                  id={`sex-${option}`}
                  label={option}
                  name="sex"
                  value={option}
                  checked={formData.sex === option}
                  onChange={handleInputChange}
                />
              ))}
            </div>
            {errors.sex && (
              <div className="text-danger" style={{ fontSize: "0.875em", marginTop: "0.25rem" }}>
                {errors.sex}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      {/* Contact + Password */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              Contact Number <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="contact_no"
              placeholder="10-digit contact number"
              value={formData.contact_no}
              onChange={handleInputChange}
              isInvalid={!!errors.contact_no}
              maxLength={10}
              size="sm"
            />
            <Form.Control.Feedback type="invalid">{errors.contact_no}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              Password{" "}
              {mode === "add"
                ? <span className="text-danger">*</span>
                : <small className="text-muted">(leave blank to keep current)</small>}
            </Form.Label>
            <div className="input-group input-group-sm">
              <Form.Control
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder={mode === "add" ? "Enter password" : "Enter new password (optional)"}
                value={formData.password}
                onChange={handleInputChange}
                isInvalid={!!errors.password}
                size="sm"
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
              </button>
              <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
            </div>
          </Form.Group>
        </Col>
      </Row>

      {/* Account + Aadhaar */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              Account Number
            </Form.Label>
            <Form.Control
              type="text"
              name="account_no"
              placeholder="Account number"
              value={formData.account_no}
              onChange={handleInputChange}
              isInvalid={!!errors.account_no}
              size="sm"
            />
            <Form.Control.Feedback type="invalid">{errors.account_no}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">
              Aadhaar Number <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="aadhar_no"
              placeholder="12-digit Aadhaar"
              value={formData.aadhar_no}
              onChange={handleInputChange}
              isInvalid={!!errors.aadhar_no}
              maxLength={12}
              size="sm"
            />
            <Form.Control.Feedback type="invalid">{errors.aadhar_no}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Address */}
      <Form.Group className="mb-2">
        <Form.Label className="mb-1 small fw-semibold">
          Address <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          name="address"
          placeholder="Full address"
          value={formData.address}
          onChange={handleInputChange}
          isInvalid={!!errors.address}
          size="sm"
        />
        <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
      </Form.Group>

      {/* Status — edit only */}
      {mode === "edit" && (
        <Form.Group className="mb-0">
          <Form.Label className="mb-1 small fw-semibold">Status</Form.Label>
          <select
            className="form-select form-select-sm"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
          >
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </Form.Group>
      )}
    </div>
  );

  // ── Tab: Employment ─────────────────────────────────────────────────────────
  const renderEmploymentTab = () => (
    <div>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">Salary Percentage</Form.Label>
            <Form.Control
              type="number"
              name="salary_percentage"
              min={0}
              max={100}
              placeholder="e.g. 80"
              value={formData.salary_percentage || ""}
              onChange={handleInputChange}
              size="sm"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small fw-semibold">Salary Duration</Form.Label>
            <select
              className="form-select form-select-sm"
              name="salary_duration_type"
              value={formData.salary_duration_type || ""}
              onChange={handleInputChange}
            >
              <option value="">Select duration</option>
              <option value="1">Daily</option>
              <option value="2">Weekly</option>
              <option value="3">Monthly</option>
            </select>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-2">
        <Form.Label className="mb-1 small fw-semibold">Joining Date</Form.Label>
        <Form.Control
          type="date"
          name="joining_date"
          value={formData.joining_date || ""}
          onChange={handleInputChange}
          size="sm"
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="mb-1 small fw-semibold">Relieve Date</Form.Label>
        <div className="d-flex align-items-center gap-3 mb-2">
          <Form.Check
            type="radio"
            id="relieve-specific"
            label="Specific date"
            name="relieve_type"
            checked={!tillDate}
            onChange={() => setTillDate(false)}
          />
          <Form.Check
            type="radio"
            id="relieve-till-date"
            label="Till Date (still working)"
            name="relieve_type"
            checked={tillDate}
            onChange={() => {
              setTillDate(true);
              setFormData((prev) => ({ ...prev, relieve_date: "" }));
            }}
          />
        </div>
        {!tillDate ? (
          <Form.Control
            type="date"
            name="relieve_date"
            value={formData.relieve_date || ""}
            onChange={handleInputChange}
            size="sm"
          />
        ) : (
          <div
            className="form-control form-control-sm bg-light text-muted"
            style={{ cursor: "default" }}
          >
            Till Date — employee is still working
          </div>
        )}
      </Form.Group>

      <Form.Group className="mb-0">
        <Form.Label className="mb-1 small fw-semibold">Service Details</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="service_details"
          placeholder="Enter service details"
          value={formData.service_details || ""}
          onChange={handleInputChange}
          size="sm"
        />
      </Form.Group>
    </div>
  );

  // ── Error badge count ───────────────────────────────────────────────────────
  const basicErrorCount = Object.keys(errors).filter((k) =>
    ["prefix", "first_name", "last_name", "email", "age", "sex",
     "account_no", "aadhar_no", "contact_no", "address", "password"].includes(k)
  ).length;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Crop Modal — rendered above the main modal */}
      {cropSrc && (
        <CropModal
          imageSrc={cropSrc}
          onCancel={() => {
            setCropSrc(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
          onCropDone={handleCropDone}
        />
      )}

      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered"
          role="document"
          onMouseDown={(e) => e.stopPropagation()}
          style={{ margin: "1.75rem auto", maxWidth: "900px" }}
        >
          <div className="modal-content">
            <Form onSubmit={handleSubmit} encType="multipart/form-data">
              {/* Header */}
              <div className="modal-header py-2">
                <h5 className="modal-title fs-6">
                  <i className={`fa ${mode === "add" ? "fa-user-plus" : "fa-user-edit"} me-2`} />
                  {mode === "add" ? "Add New Lab" : "Edit Lab"}
                </h5>
                <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
              </div>

              {/* Tabs */}
              <div className="px-3 pt-2" style={{ borderBottom: "1px solid #dee2e6" }}>
                <ul className="nav nav-tabs border-0">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link py-2 ${activeTab === "basic" ? "active fw-semibold" : "text-secondary"}`}
                      onClick={() => setActiveTab("basic")}
                      style={{
                        border: "none",
                        borderBottom: activeTab === "basic" ? "2px solid #0d6efd" : "2px solid transparent",
                      }}
                    >
                      <i className="fa fa-user me-1" />
                      Basic Info
                      {basicErrorCount > 0 && (
                        <span className="badge bg-danger ms-1" style={{ fontSize: "0.7rem" }}>
                          {basicErrorCount}
                        </span>
                      )}
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link py-2 ${activeTab === "employment" ? "active fw-semibold" : "text-secondary"}`}
                      onClick={() => setActiveTab("employment")}
                      style={{
                        border: "none",
                        borderBottom: activeTab === "employment" ? "2px solid #0d6efd" : "2px solid transparent",
                      }}
                    >
                      <i className="fa fa-briefcase me-1" />
                      Employment
                    </button>
                  </li>
                </ul>
              </div>

              {/* Body */}
              <div className="modal-body p-3">
                {activeTab === "basic"      && renderBasicTab()}
                {activeTab === "employment" && renderEmploymentTab()}
              </div>

              {/* Footer */}
              <div className="modal-footer py-2">
                <div className="me-auto">
                  <small className="text-muted">
                    <i className="fa fa-info-circle me-1" />
                    User will be assigned as <strong>Type {authUserTypeId + 1}</strong>
                  </small>
                </div>
                <Button variant="secondary" onClick={onClose} disabled={loading} size="sm">
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={loading} size="sm">
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save me-1" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserModal;